import { adoptSession, dropSiteSession, mountWidget, syncSiteLogin } from './ui/widget.js';
import { fetchRemoteConfig } from './api/client.js';
import type { SiteLogin, StoredSession, WidgetConfig } from './ui/types.js';

// The host page can set window.LCW_CONFIG before including this script.
declare global {
  interface Window {
    LCW_CONFIG?: Partial<WidgetConfig>;
    /** Set by the hosted chat page: a session earned via an emailed link. */
    LCW_SESSION?: StoredSession;
    /** Set by a website that signs its own logged-in users in. See ui/types.ts. */
    LCW_SITE?: SiteLogin;
    LCW_AUTO_OPEN?: boolean;
    LCW_STANDALONE?: boolean;
    LiveChatWidget?: { mount: (config?: Partial<WidgetConfig>) => void };
  }
}

/**
 * Where this bundle was served from.
 *
 * The backend serves the widget at /widget/chat-widget.js, so the script's own
 * origin *is* the API. Deriving it means a site owner pastes one tag and it
 * works. The alternative -- requiring a window.LCW_CONFIG block first -- fails
 * silently when it is forgotten: the widget falls back to localhost, renders
 * perfectly, and every request dies in the visitor's browser.
 *
 * Read at module scope on purpose. document.currentScript is only set while
 * the script is executing; by the time boot() runs on DOMContentLoaded it is
 * null.
 */
const servedFrom: string | null = (() => {
  const self = document.currentScript as HTMLScriptElement | null;
  const src =
    self?.src ||
    document.querySelector<HTMLScriptElement>('script[src*="chat-widget"]')?.src;
  if (!src) return null;
  try {
    return new URL(src, window.location.href).origin;
  } catch {
    return null;
  }
})();

/**
 * Used when the backend cannot be reached at all. Not the normal path: almost
 * every field here is overridden by the admin's settings a moment later.
 */
const fallbackConfig: WidgetConfig = {
  apiUrl: servedFrom ?? 'http://localhost:4000',
  brandName: 'Support',
  brandColor: '',
  welcome: 'Hi! How can we help?',
  hoverMessage: '',
  position: 'bottom-right',
  theme: 'auto',
  sideSpacing: 20,
  bottomSpacing: 20,
  showOnMobile: true,
  logoUrl: '',
  useMascot: true,
  emoji: { enabled: true, list: ['👍', '❤️', '😂', '😮', '😢', '🙏'], trigger: 'click' },
};

function isNarrowViewport(): boolean {
  return window.matchMedia('(max-width: 640px)').matches;
}

/**
 * Settings from the admin console win over window.LCW_CONFIG.
 *
 * That is the whole point of the Settings page: changing the greeting should
 * not require every customer to edit their site. LCW_CONFIG still fills
 * anything the server did not answer with, which is what keeps the widget
 * usable when the config request fails.
 */
async function boot() {
  const local: WidgetConfig = { ...fallbackConfig, ...(window.LCW_CONFIG ?? {}) };
  const remote = await fetchRemoteConfig(local.apiUrl);

  if (remote && !remote.allowed) {
    // Loud on purpose. Whoever pasted the snippet is the person looking at this
    // console, and silence would leave them guessing why nothing appeared.
    // eslint-disable-next-line no-console
    console.warn(
      `[Chatloom] ${remote.message ?? 'This domain is not verified.'}` +
        (remote.adminUrl ? ` Console: ${remote.adminUrl}` : ''),
    );
    return;
  }

  const config: WidgetConfig = { ...local, ...(remote?.config ?? {}) };

  // A master switch in the console, so chat can be taken down without asking
  // anyone to edit their website.
  if (remote?.config?.enabled === false) return;
  if (!config.showOnMobile && isNarrowViewport()) return;

  // The hosted chat page hands us a session it already proved, via a link sent
  // to an address the visitor demonstrably controls. Adopt it before mounting
  // so the widget opens on the conversation rather than the sign-in form.
  if (window.LCW_SESSION?.visitorToken) adoptSession(window.LCW_SESSION);
  // A website that signs its own users in: make the stored sign-in match
  // whoever is logged in there now, before anything is drawn. Costs nothing
  // when the answer is already stored, which is nearly every page.
  else if (window.LCW_SITE) await syncSiteLogin(window.LCW_SITE);
  else dropSiteSession();

  mountWidget(config, {
    autoOpen: window.LCW_AUTO_OPEN === true,
    standalone: window.LCW_STANDALONE === true,
  });
}

window.LiveChatWidget = {
  mount: (override) => mountWidget({ ...fallbackConfig, ...(window.LCW_CONFIG ?? {}), ...(override ?? {}) }),
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void boot(), { once: true });
} else {
  void boot();
}
